<template>
  <q-card flat style="max-width: 600vw; max-height: 80vh">
    <q-card-section>
      <div class="text-h6 row items-center">Unit Mix Import</div>
      <div class="q-mt-xs text-grey-7">
        Take advantage of our upload feature to quickly import a new development's Unit Mix from a
        CSV file.
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section class="row justify-between items-center q-col-gutter-md">
      <div class="col">
        <div class="text-weight-medium">CSV Template</div>
        <div class="text-grey-7">
          Download the CSV template and fill in the required columns to import your units.
        </div>
      </div>
      <div>
        <q-btn
          id="unit-mix-template_download"
          outline
          unelevated
          label="Download"
          no-caps
          :href="sampleCSVURL"
          download="hellodata-unit-mix-upload-template.csv"
        />
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section class="row justify-between items-center">
      <div class="q-mb-sm">
        <div class="text-weight-medium">Upload Unit Mix CSV</div>
        <div class="text-grey-7">Upload the Data CSV file to import units.</div>
      </div>
      <q-file
        v-model="file"
        label="Select a CSV file"
        accept=".csv"
        style="min-width: 300px"
        color="primary"
        outlined
        dense
        @update:model-value="uploaded"
      />
    </q-card-section>
    <q-separator />
    <template v-if="errors.length">
      <q-card-section>
        <div class="text-weight-medium">Errors</div>
        <ul class="text-negative" style="max-height: 100px; overflow-y: auto">
          <li v-for="err in errors" :key="err">{{ err }}</li>
        </ul>
      </q-card-section>
      <q-separator />
    </template>
    <q-separator />
    <q-card-actions align="right">
      <q-btn v-close-popup unelevated label="Close" no-caps />
      <q-btn
        v-if="file"
        unelevated
        :label="`Import ${floorplans.length} Units`"
        no-caps
        color="primary"
        @click="useFloorplans"
      />
    </q-card-actions>
  </q-card>
</template>

<script lang="ts">
import Papa from 'papaparse'

// Similar to `type Floorplan` found on `CardEditProperty.vue`
interface CSVRow {
  beds: number
  baths: number
  name: string
  units: number
  avg_sqft: number | string | null
  avg_rent: number | string | null
}

export default {
  name: 'UnitMixCsvImport',
  components: {},
  emits: ['finished'],
  data() {
    return {
      file: null as File | null,
      floorplans: [] as CSVRow[],
      errors: [] as string[],
      sampleCSVURL:
        'https://storage.googleapis.com/hello-data-ai-public-assets/hellodata-unit-mix-upload-template.csv'
    }
  },
  methods: {
    useFloorplans() {
      this.$tracker.unitMixCsvImport()
      this.$emit('finished', this.floorplans)

      // reset the state
      this.floorplans = []
    },
    uploaded() {
      // reset the state
      this.floorplans = []
      this.errors = []

      if (!this.file) {
        return
      }

      Papa.parse(this.file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: (results: {
          data: Record<string, unknown>[]
          errors: { row: number; message: string }[]
        }) => {
          // report any errors if they're present
          this.errors = results.errors.map((err) => `Row ${err.row}: ${err.message}`)
          if (results.data.length === 0) {
            this.errors.push('CSV file is empty')
            return
          }

          const requiredColumns = ['beds', 'baths', 'units', 'avg_sqft'] as (keyof CSVRow)[]

          // verify the required columns exist
          for (const col of requiredColumns) {
            if (results.data[0][col] === undefined) {
              this.errors.push(`CSV file must have column: ${col}`)
              return
            }
          }

          const validRows: CSVRow[] = []
          for (let i = 0; i < results.data.length; i++) {
            const row = results.data[i] as unknown as CSVRow

            // if bed or bath is not a number
            if (typeof row.beds !== 'number') {
              this.errors.push(
                `Row ${i + 1}: beds must be a number (got ${String(row.beds)}). Ignoring row.`
              )
              continue
            }
            if (typeof row.baths !== 'number') {
              this.errors.push(
                `Row ${i + 1}: baths must be a number (got ${String(row.baths)}). Ignoring row.`
              )
              continue
            }
            if (typeof row.units !== 'number') {
              this.errors.push(
                `Row ${i + 1}: units must be a number (got ${String(row.units)}). Ignoring row.`
              )
              continue
            }

            if (typeof row.avg_sqft !== 'number') {
              this.errors.push(
                `Row ${i + 1}: avg_sqft must be a number (got ${String(row.avg_sqft)}). Ignoring row.`
              )
              continue
            }
            if (typeof row.avg_rent !== 'number') {
              this.errors.push(
                `Row ${i + 1}: avg_rent must be a number (got ${String(row.avg_rent)}). Ignoring row.`
              )
              continue
            }

            // suggest a name if they have not provided one
            if (!row.name && row.beds && row.baths) {
              row.name = `${row.beds}BR/${row.baths}BA`
            }

            if (typeof row.name === 'number') {
              row.name = String(row.name)
            }

            validRows.push(row)
          }

          this.floorplans = validRows
        }
      })
    }
  }
}
</script>
